import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AccountCircle from "@material-ui/icons/AccountCircle";
import Brightness4Icon from "@material-ui/icons/Brightness4";

import SubscriptionNotice from "../components/SubscriptionNotice";
import BrandFooter from "../components/BrandFooter";
import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";

import logo from "../assets/zapticket_ligth.png";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    "@supports (height: 100dvh)": { height: "100dvh" },
    overflow: "hidden",
    backgroundColor: theme.palette.background.default,
  },
  toolbar: {
    paddingRight: 16,
    [theme.breakpoints.down("sm")]: { paddingLeft: 12, paddingRight: 8 },
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 8px",
    minHeight: "48px",
    // Ajuste dinâmico do degradê no topo
    background: theme.palette.type === 'light' 
      ? "linear-gradient(180deg, #14583e 0%, #0b3024 100%)" 
      : "linear-gradient(to bottom, #1a1a1a 0%, #000000 100%)",
    color: "#ffffff",
    ...theme.mixins.toolbar,
  },
  logo: {
    width: "80%",
    maxHeight: "40px",
    objectFit: "contain",
    backgroundColor: "transparent",
    filter: "brightness(0) invert(1)", // Torna o logo branco para o degradê
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    [theme.breakpoints.down("sm")]: { zIndex: theme.zIndex.appBar },
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    background: theme.app.gradient,
    color: "#ffffff",
    borderBottom: "1px solid rgba(255,255,255,.12)",
    boxShadow: "none",
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 16,
    [theme.breakpoints.down("xs")]: { marginRight: 8 },
    color: "#d5f1e2", 
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    minWidth: 0,
    [theme.breakpoints.down("xs")]: { fontSize: 16 },
    color: "#ffffff",
    fontWeight: "bold",
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    // Ajuste dinâmico do degradê lateral
    background: theme.palette.type === 'light' 
      ? "linear-gradient(180deg, #14583e 0%, #0b3024 100%)" 
      : "linear-gradient(to bottom, #1a1a1a 0%, #000000 100%)",
    color: "#ffffff",
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    background: theme.palette.type === 'light' 
      ? "linear-gradient(180deg, #14583e 0%, #0b3024 100%)" 
      : "linear-gradient(to bottom, #1a1a1a 0%, #000000 100%)",
  },
  appBarSpacer: {
    minHeight: "51px",
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  page: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    padding: 16,
    [theme.breakpoints.down("sm")]: { padding: 8 },
  },
  iconButton: {
    color: "#d5f1e2",
    "&:hover": {
      color: "#ffffff",
    },
  },
  chevronIcon: {
    color: "#ffffff",
  }
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { pathname } = useLocation();
  const isChat = /^\/tickets(?:\/|$)/.test(pathname);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { toggleTheme } = useThemeContext();

  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const handleMenu = (event) => { setAnchorEl(event.currentTarget); setMenuOpen(true); };
  const handleCloseMenu = () => { setAnchorEl(null); setMenuOpen(false); };
  const handleOpenUserModal = () => { setUserModalOpen(true); handleCloseMenu(); };
  const handleClickLogout = () => { handleCloseMenu(); handleLogout(); };

  if (loading) return <BackdropLoading />;

  return (
    <div className={classes.root}>
      <UserModal open={userModalOpen} onClose={() => setUserModalOpen(false)} userId={user.id} />
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={isMobile ? { style: { position: "fixed" } } : undefined}
        classes={{ paper: clsx(classes.drawerPaper, !isMobile && !drawerOpen && classes.drawerPaperClose) }}
        open={drawerOpen}
      >
        <div className={classes.toolbarIcon}>
          {drawerOpen && <img src={logo} alt="ZapTicket" className={classes.logo} />}
          <IconButton aria-label="Fechar menu" onClick={() => setDrawerOpen(!drawerOpen)}>
            <ChevronLeftIcon className={classes.chevronIcon} />
          </IconButton>
        </div>
        <Divider style={{ backgroundColor: "rgba(255,255,255,0.1)" }} />
        <List>
          <MainListItems drawerClose={() => isMobile && setDrawerOpen(false)} />
        </List>
      </Drawer>

      <AppBar position="fixed" style={!isMobile && !drawerOpen ? { marginLeft: 56, width: "calc(100% - 56px)" } : undefined} className={clsx(classes.appBar, !isMobile && drawerOpen && classes.appBarShift)}>
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton aria-label="Abrir menu" edge="start" onClick={() => setDrawerOpen(!drawerOpen)} className={clsx(classes.menuButton, !isMobile && drawerOpen && classes.menuButtonHidden)}>
            <MenuIcon />
          </IconButton>
          <Typography component="h1" variant="h6" noWrap className={classes.title}>
            ZapTicket
          </Typography>
          <IconButton aria-label="Alternar tema" onClick={toggleTheme} className={classes.iconButton}>
            <Brightness4Icon />
          </IconButton>
          {user.id && <NotificationsPopOver />}
          <IconButton aria-label="Minha conta" onClick={handleMenu} className={classes.iconButton}>
            <AccountCircle />
          </IconButton>
          <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleCloseMenu}>
            <MenuItem onClick={handleOpenUserModal}>{i18n.t("mainDrawer.appBar.user.profile")}</MenuItem>
            <MenuItem onClick={handleClickLogout}>{i18n.t("mainDrawer.appBar.user.logout")}</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <SubscriptionNotice user={user} />
        <div className={classes.page} style={isChat ? { padding: 0, overflow: "hidden" } : undefined}>{children}</div>
        <BrandFooter />
      </main>
    </div>
  );
};

export default LoggedInLayout;
